import React, { memo, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { SEGMENT_DISTANCES } from '../../constants';
import { LapToggleSwitch } from './';
import { useStopwatchContext } from '../../contexts';
import { Segment } from '../../types';
import { Lap } from '../../types/stopwatch';
import { triggerVibration } from '../../utils/hapticsUtils';

interface DistanceButtonsProps {
  isRunning: boolean;
  onRecordSegment: (distance: number) => { segment: Segment | null; lapPace: number | null };
  currentSegmentDistance?: number | null;
}

const DistanceButtons: React.FC<DistanceButtonsProps> = memo(
  ({ isRunning, onRecordSegment, currentSegmentDistance = 0 }) => {
    // Get context functions (recordSegment now has new signature)
    const {
      isAutoLapEnabled,
      toggleAutoLap,
      recordLap,
      resetLapDataPreservingPace,
      setLastSegmentPace,
      setLastCompletedLapPace,
      addCompletedLapToSession,
    } = useStopwatchContext();

    // Memoized handler creation with auto-lap functionality
    const createDistanceHandler = useCallback(
      (distance: number) => () => {
        triggerVibration();
        if (isRunning) {
          // Record segment first and get the result { segment, lapPace }
          const { segment: segmentResult, lapPace: calculatedLapPace } = onRecordSegment(distance);

          // If auto-lap is enabled and we successfully recorded a segment, also trigger lap
          if (isAutoLapEnabled && segmentResult) {
            // When auto-lap is on, we need to make sure we preserve both:
            // 1. The lastSegmentPace (showing the segment that triggered the lap)
            // 2. The combined pace for the completed lap (which now becomes the "Last Lap Pace")

            // Capture the current segment's pace that triggered the lap
            const segmentPace = segmentResult?.pace;

            // Use the DIRECTLY RETURNED lap pace from recordSegment
            const completedLapPace = calculatedLapPace;

            // Important: We must ensure the last completed lap pace is set correctly BEFORE
            // any other state changes happen. Use flushSync to make this update immediate.
            if (completedLapPace !== null && isFinite(completedLapPace)) {
              flushSync(() => {
                setLastCompletedLapPace(completedLapPace);
              });
            } else {
              // Ensure it's set to null if the calculation wasn't valid
              flushSync(() => {
                setLastCompletedLapPace(null);
              });
            }

            // Now reset lap data while preserving pace info
            resetLapDataPreservingPace(completedLapPace);

            // Explicitly set the last segment pace to the current segment's pace
            if (segmentPace !== null && isFinite(segmentPace as number)) {
              setLastSegmentPace(segmentPace);
            }

            // Create a custom function to be passed to recordLap
            // This ensures the lap is properly added to the session with its segment data
            const customLapCallback = (lap: Lap) => {
              try {
                // Ensure the pace value is explicitly set before adding to session
                if (completedLapPace !== null && isFinite(completedLapPace)) {
                  // Directly set the pace value on the lap object
                  lap.pace = completedLapPace;
                  console.log('Set lap pace for lap', lap.number, 'to', completedLapPace);
                } else {
                  console.warn('No valid pace available for lap', lap.number);
                }

                // Add the modified lap to the session
                addCompletedLapToSession(lap);
              } catch (error) {
                console.error('Error in customLapCallback:', error);
                // Still try to add the lap even if pace setting failed
                addCompletedLapToSession(lap);
              }
            };

            // Then record the lap, which will advance to the next lap number
            recordLap(customLapCallback);
          }
        }
      },
      [
        isRunning,
        onRecordSegment,
        isAutoLapEnabled,
        recordLap,
        resetLapDataPreservingPace,
        setLastSegmentPace,
        setLastCompletedLapPace,
        addCompletedLapToSession,
      ]
    );

    // Helper to determine button className based on status
    const getButtonClass = useCallback(
      (distance: number): string => {
        // Base class
        let className = 'workout__distance-button';

        // Add state classes - NOT adding a disabled class as we use the HTML disabled attribute
        if (currentSegmentDistance === distance) {
          className += ' workout__distance-button--active';
        }

        // Add auto-lap class when enabled
        if (isAutoLapEnabled) {
          className += ' workout__distance-button--auto-lap';
        }

        return className;
      },
      [currentSegmentDistance, isAutoLapEnabled]
    );

    return (
      <>
        <LapToggleSwitch isEnabled={isAutoLapEnabled} onToggle={toggleAutoLap} />

        <div className="workout__distance-container">
          <div className="workout__distance-row">
            <button
              className={getButtonClass(SEGMENT_DISTANCES.SHORT)}
              onClick={createDistanceHandler(SEGMENT_DISTANCES.SHORT)}
              disabled={!isRunning}
              data-running={isRunning ? 'true' : 'false'}
            >
              {SEGMENT_DISTANCES.SHORT}m
            </button>
            <button
              className={getButtonClass(SEGMENT_DISTANCES.MEDIUM)}
              onClick={createDistanceHandler(SEGMENT_DISTANCES.MEDIUM)}
              disabled={!isRunning}
              data-running={isRunning ? 'true' : 'false'}
            >
              {SEGMENT_DISTANCES.MEDIUM}m
            </button>
          </div>

          <div className="workout__distance-row">
            <button
              className={getButtonClass(300)}
              onClick={createDistanceHandler(300)}
              disabled={!isRunning}
              data-running={isRunning ? 'true' : 'false'}
            >
              300m
            </button>
            <button
              className={getButtonClass(SEGMENT_DISTANCES.LONG)}
              onClick={createDistanceHandler(SEGMENT_DISTANCES.LONG)}
              disabled={!isRunning}
              data-running={isRunning ? 'true' : 'false'}
            >
              {SEGMENT_DISTANCES.LONG}m
            </button>
          </div>
        </div>
      </>
    );
  }
);

// Add display name
DistanceButtons.displayName = 'DistanceButtons';

export default DistanceButtons;
